import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { validateBotVersionName, getDefaultErrorMessage } from '../../../utils/botValidation';
import BotSessionData from '../../../utils/botSessionStorage';

export const useCreateBotVersion = () => {
    const intl = useIntl();
    const history = useHistory();

    const createNewVersion = useCallback((currentBot, currentBotId = null) => {
        const existingVersions = currentBot.config?.versions || [currentBot.config?.version || "DEFAULT"];

        let newVersionName = null;
        let isValidName = false;

        // Validation loop - keep prompting until valid name or user cancels
        while (!isValidName) {
            newVersionName = prompt(
                intl.formatMessage({
                    id: 'bot.version.createNew.prompt',
                    defaultMessage: 'Enter a name for the new version:'
                })
            );

            // User cancelled
            if (newVersionName === null) {
                return false;
            }

            // Validate the name
            const validation = validateBotVersionName(newVersionName, existingVersions);

            if (validation.isValid) {
                isValidName = true;
            } else {
                // Show error and loop again
                alert(intl.formatMessage({
                    id: validation.errorMessageId,
                    defaultMessage: getDefaultErrorMessage(validation.errorType)
                }));
            }
        }

        // At this point, we have a valid version name
        const trimmedName = newVersionName.trim();

        // Build the versions array including the new version
        const updatedVersions = [...existingVersions];
        if (!updatedVersions.includes(trimmedName)) {
            updatedVersions.push(trimmedName);
        }

        // Create a template from the current bot with the new version name
        const templateBotData = {
            ...currentBot,
            config: {
                ...currentBot.config,
                version: trimmedName,
                versionGroup: currentBot.config?.versionGroup,
                versions: updatedVersions,
                status: "DRAFT"
            }
        };

        // Remove the ID since we're creating a new bot
        delete templateBotData.id;

        // Store in session storage with a special key
        sessionStorage.setItem('newBotTemplate', JSON.stringify(templateBotData));

        // Clear any existing session data for the current bot if we have an ID
        if (currentBotId) {
            BotSessionData.clear(currentBotId);
        }

        // Determine the current tab to maintain the same view
        const currentPath = history.location.pathname;
        let currentTab = '/information'; // Default tab

        if (currentPath.includes('/instructions')) {
            currentTab = '/instructions';
        } else if (currentPath.includes('/materials')) {
            currentTab = '/materials';
        } else if (currentPath.includes('/information')) {
            currentTab = '/information';
        }

        // Navigate to new bot page with the same tab
        history.push(`/support-bot/new${currentTab}`);

        return true; // Success
    }, [intl, history]);

    return { createNewVersion };
};